import { Button, Stack, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { SimplePageWrapper } from '~/@ui/simple-page-wrapper/SimplePageWrapper'
import DocumentsBlackSvg from '~/@ui/_sharedSvgs/ui/DocumentsBlack.svg?react'
import TrashCanSvg from '~/@ui/_sharedSvgs/ui/TrashCan.svg?react'
import { useAlert } from '~/@ui/alert/useAlert'
import { useEffect, useRef, useState } from 'react'
import { getApi } from '~/@utils/api/getApi'
import { getErrorMessage } from '~/@utils/api/getErrorMessage'
import { useUploadDocuments } from './useUploadDocuments'

export const DocumentsUploadPage = () => {
  const { orderId = '' } = useParams()
  const navigate = useNavigate()
  const { showAlert } = useAlert()
  const { isUploading, uploadDocuments } = useUploadDocuments()

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [estateId, setEstateId] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    if (fileList && fileList.length > 0) {
      const filesArray = Array.from(fileList)

      setSelectedFiles((prevFiles) => [...prevFiles, ...filesArray])
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      showAlert('error', 'Prosím vyberte aspoň jeden dokument')
      return
    }

    try {
      await uploadDocuments(estateId, selectedFiles)
      navigate('/vlastnik/portfolio/overview')
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  const getOrder = async () => {
    try {
      const { data: response } =
        await getApi().ownerPensionOrderAppApiControllerGetOwnerPensionOrder(
          orderId
        )

      if ('success' in response) {
        setEstateId(response.ownerPensionOrder.estateId || '')
        return
      }

      throw new Error('Nastala chyba pri načítavaní dát o objednávke.')
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  useEffect(() => {
    if (!orderId) {
      navigate('/vlastnik/dotaznik/adresa-nehnutelnosti')
      return
    }

    getOrder()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <SimplePageWrapper
      onClickBack={() =>
        navigate(`/vlastnik/dotaznik/potrebne-dokumenty/${orderId}`)
      }>
      <Stack className="px-5 pb-28">
        <Stack className="mt-[26px]">
          <Typography className="text-2xl font-bold text-zBlack200">
            Potrebné dokumenty
          </Typography>
          <Typography className="text-3d font-medium text-zGrey400 mt-3">
            *Výpis z bankového účtu
          </Typography>
          <Typography className="text-3d font-medium text-zGrey400 mt-1">
            *List vlastníctva
          </Typography>
          <Typography className="text-3d font-medium text-zGrey400 mt-3">
            Dokumenty môžete poslať vo forme jedného alebo viacerych dokumentov.
          </Typography>
        </Stack>

        {/* Zobrazenie vybraných súborov */}
        {selectedFiles.length > 0 && (
          <Stack className="gap-3 mt-8">
            {selectedFiles.map((file, index) => (
              <Stack
                key={`${file.name}-${index}`}
                className="flex-row w-full justify-between items-center p-4 pr-3 bg-zGrey200 rounded-4d">
                <Typography className="text-3d font-medium text-zBlack200">
                  {file.name}
                </Typography>
                <Button
                  onClick={() => handleRemoveFile(index)}
                  className="w-6 h-6 p-0 min-w-0"
                  disabled={isUploading}>
                  <TrashCanSvg />
                </Button>
              </Stack>
            ))}
          </Stack>
        )}

        <Stack className="items-center gap-6 p-6 mt-8 bg-zGrey200 rounded-4d">
          <Button
            variant="contained"
            className="w-fit gap-2 px-6 bg-white text-zBlack200"
            onClick={handleUploadClick}
            disabled={isUploading}>
            <DocumentsBlackSvg />
            {selectedFiles.length > 0
              ? 'Pridať ďalšie dokumenty'
              : 'Nahrať dokumenty'}
          </Button>

          <Typography className="text-xs font-bold text-zGrey550">
            Nahrať dokument vo formáte PDF
          </Typography>
        </Stack>

        <Button
          variant="contained"
          className="w-fit h-14 self-center px-12 mt-[72px]"
          onClick={handleSubmit}
          disabled={isUploading || selectedFiles.length === 0}>
          {isUploading ? 'Nahrávanie súborov...' : 'Nahrať a pokračovať'}
        </Button>

        <input
          type="file"
          ref={fileInputRef}
          accept=".pdf"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Stack>
    </SimplePageWrapper>
  )
}
